import type { MessageThread } from '@emr/shared';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../components/primitives.tsx';
import { PatientLabel } from '../components/PatientLabel.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDateAndTime } from '../format/formatDateTime.ts';
import type { OrgMember } from '../api/orgMembers.ts';
import { isAssignedToViewer, isClaimable } from './threadRules.ts';

/**
 * The inbox's own thread list — a card list, not a table (`WaitlistPanel.tsx`'s own reasoning for
 * the same choice applies identically: no single unbounded column under the same pressure a wide
 * table has, so this needs no `useHasRoomForTable`-shaped breakpoint of its own). Rendered by
 * `routes/InboxPage.tsx` on both sides of `useHasRoomForInboxSplitView` — as the left pane on
 * desktop, or as the whole page on a phone until a thread is selected.
 *
 * `subject` is patient-entered, unbounded free text (`MAX_MESSAGE_SUBJECT_LENGTH`, `@emr/shared`) —
 * `break-words [overflow-wrap:anywhere]`, matching `components/primitives.tsx`'s own `DetailRow`,
 * per `claude-kit/rules/invariants.md`'s table/long-content strategy applied to this non-table list.
 */
export function ThreadList({
  threads,
  selectedThreadId,
  onSelect,
  viewerUserId,
  members,
}: {
  readonly threads: readonly MessageThread[];
  readonly selectedThreadId: string | null;
  readonly onSelect: (threadId: string) => void;
  readonly viewerUserId: string;
  readonly members: readonly OrgMember[] | undefined;
}) {
  const { t } = useTranslation('inbox');

  if (threads.length === 0) {
    return <p className="m-0 text-sm text-ink-muted">{t('page.list.empty')}</p>;
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {threads.map((thread) => (
        <ThreadRow
          key={thread.id}
          thread={thread}
          selected={thread.id === selectedThreadId}
          onSelect={onSelect}
          viewerUserId={viewerUserId}
          members={members}
        />
      ))}
    </ul>
  );
}

function ThreadRow({
  thread,
  selected,
  onSelect,
  viewerUserId,
  members,
}: {
  readonly thread: MessageThread;
  readonly selected: boolean;
  readonly onSelect: (threadId: string) => void;
  readonly viewerUserId: string;
  readonly members: readonly OrgMember[] | undefined;
}) {
  const { t } = useTranslation('inbox');
  const { preference } = useDateTimeFormat();

  const kindTone: BadgeTone = thread.kind === 'care_team' ? 'accent' : 'neutral';
  const statusTone: BadgeTone = thread.status === 'open' ? 'success' : 'neutral';

  const assignedLabel =
    thread.kind === 'general'
      ? isAssignedToViewer(thread, viewerUserId)
        ? t('page.list.assignedToYou')
        : thread.assignedToUserId === null
          ? null // rendered as the "Unassigned" badge below instead of plain text
          : (members?.find((member) => member.userId === thread.assignedToUserId)?.email ??
            t('page.list.assignedToOtherFallback'))
      : null;

  return (
    <li>
      <button
        type="button"
        onClick={() => {
          onSelect(thread.id);
        }}
        aria-current={selected ? 'true' : undefined}
        className={[
          'flex w-full flex-col gap-1.5 rounded-panel border p-3 text-left text-sm transition-colors',
          selected
            ? 'border-accent-text bg-raised'
            : 'border-line bg-surface hover:border-line-strong hover:bg-raised',
        ].join(' ')}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <PatientLabel
            patientId={thread.patientId}
            loadingLabel={t('page.list.loadingPatient')}
            unknownLabel={t('page.list.unknownPatient')}
          />
          <span className="text-xs text-ink-muted">
            {formatDateAndTime(thread.updatedAt, preference)}
          </span>
        </div>

        <p className="m-0 text-ink break-words [overflow-wrap:anywhere]">
          {thread.subject ?? t('page.list.noSubject')}
        </p>

        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={kindTone}>{t(`page.list.kindBadge.${thread.kind}`)}</Badge>
          {thread.category !== null ? (
            <Badge tone="neutral">{t(`page.list.category.${thread.category}`)}</Badge>
          ) : null}
          <Badge tone={statusTone}>{t(`page.list.statusBadge.${thread.status}`)}</Badge>
          {isClaimable(thread) ? (
            <Badge tone="warning">{t('page.list.unassignedBadge')}</Badge>
          ) : null}
          {assignedLabel !== null ? (
            <span className="text-xs text-ink-muted">{assignedLabel}</span>
          ) : null}
        </div>
      </button>
    </li>
  );
}
